import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import style from "../../../styles/dashboard-admin.module.scss";
import { useAuth } from "@clerk/clerk-react";
import { Booking } from "../../../types/BookingWithCert";
import { getFutureBookingsPaginated, getPastBookingsPaginated, getFutureRemedialBookingsPaginated, getPastRemedialBookingsPaginated } from "../../../utils/funcs/api/get-admin-bookings";
import { getServerURL } from "../../../utils/funcs/get-serverURL";
import { formatDisplayDate } from "../../../utils/funcs/formatDisplayDate";
import { capitaliseFirstLetter } from "../../../utils/funcs/capitalise-first-letter";
import LoadingSpinner from "../../loading-spinner";

export default function AdminBookings({ user, role }: any) {
  const { getToken } = useAuth();
  const [selectedBookingForModal, setSelectedBookingForModal] = useState<Booking | null>(null);
  const [selectedEICRRecord, setSelectedEICRRecord] = useState<any>(null);
  const [selectedEICRPdfUrl, setSelectedEICRPdfUrl] = useState<string | null>(null);
  const [loadingEICRRecord, setLoadingEICRRecord] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState<Booking | null>(null);
  const [cancelConfirmText, setCancelConfirmText] = useState("");
  const [cancellationInProgress, setCancellationInProgress] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [pastBookings, setPastBookings] = useState<Booking[]>([]);
  const [futureBookings, setFutureBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState<"all" | "teck" | "myconstructor">("all");
  const [eicrStatusMap, setEicrStatusMap] = useState<Record<string, { result: string; found: boolean; hasCertificate: boolean }>>({});
  const [currentPageFuture, setCurrentPageFuture] = useState(1);
  const [currentPagePast, setCurrentPagePast] = useState(1);
  const [totalPagesPast, setTotalPagesPast] = useState(1);
  const [totalPagesFuture, setTotalPagesFuture] = useState(1);
  const [paginating, setPaginating] = useState(false);
  const itemsPerPage = 10;

  const serverURL = getServerURL();

  // Fetch a page of future bookings (merges regular + remedial)
  const fetchFuturePage = useCallback(
    async (page: number) => {
      try {
        setPaginating(true);
        const [regularResult, remedialResult] = await Promise.all([
          getFutureBookingsPaginated({ getToken, serverURL, page, limit: itemsPerPage }),
          getFutureRemedialBookingsPaginated({ getToken, serverURL, page, limit: itemsPerPage })
        ]);
        
        // Merge and sort by date
        const merged = [...regularResult.bookings, ...remedialResult.bookings].sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateA - dateB;
        });
        
        setFutureBookings(merged);
        // Use the max pages from either source
        const maxPages = Math.max(regularResult.pagination.totalPages, remedialResult.pagination.totalPages);
        setTotalPagesFuture(maxPages);
      } catch (error) {
        console.error("Error fetching future bookings:", error);
      } finally {
        setPaginating(false);
      }
    },
    [getToken, serverURL, itemsPerPage]
  );

  // Fetch a page of past bookings (merges regular + remedial)
  const fetchPastPage = useCallback(
    async (page: number) => {
      try {
        setPaginating(true);
        const [regularResult, remedialResult] = await Promise.all([
          getPastBookingsPaginated({ getToken, serverURL, page, limit: itemsPerPage }),
          getPastRemedialBookingsPaginated({ getToken, serverURL, page, limit: itemsPerPage })
        ]);
        
        // Merge and sort by date (descending for past)
        const merged = [...regularResult.bookings, ...remedialResult.bookings].sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return dateB - dateA;
        });
        
        setPastBookings(merged);
        setTotalPagesPast(Math.max(regularResult.pagination.totalPages, remedialResult.pagination.totalPages));
        fetchEICRStatusForBookings(regularResult.bookings);
      } catch (error) {
        console.error("Error fetching past bookings:", error);
      } finally {
        setPaginating(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getToken, serverURL, itemsPerPage]
  );

  const fetchEICRStatusForBookings = useCallback(
    async (bookings: Booking[]) => {
      if (bookings.length === 0) return;

      try {
        const token = await getToken();
        const eicrMap: Record<string, { result: string; found: boolean; hasCertificate: boolean }> = {};

        for (const booking of bookings) {
          try {
            const response = await axios.get(
              `${serverURL}/eicr-records/booking/${booking.bookingID}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (response.data && response.data.record) {
              eicrMap[booking.bookingID] = {
                result: response.data.record.result || "Unknown",
                found: true,
                hasCertificate: !!response.data.record.eicrFileUrl,
              };
            } else {
              eicrMap[booking.bookingID] = { result: "", found: false, hasCertificate: false };
            }
          } catch (error) {
            eicrMap[booking.bookingID] = { result: "", found: false, hasCertificate: false };
          }
        }

        setEicrStatusMap((prev) => ({ ...prev, ...eicrMap }));
      } catch (error) {
        console.error("Error fetching EICR status:", error);
      }
    },
    [getToken, serverURL]
  );

  useEffect(() => {
    const initialLoad = async () => {
      await Promise.all([fetchFuturePage(1), fetchPastPage(1)]);
      setLoading(false);
    };
    initialLoad();
  }, [fetchFuturePage, fetchPastPage]);

  const toggleBooking = useCallback(
    async (booking: Booking) => {
      setSelectedBookingForModal(booking);
      setSelectedEICRRecord(null);
      setLoadingEICRRecord(true);
      try {
        // Don't fetch EICR for remedial bookings - they are the remedial works themselves
        if ((booking as any).isRemedialBooking) {
          setLoadingEICRRecord(false);
          return;
        }
        
        const token = await getToken();
        const response = await axios.get(
          `${serverURL}/eicr-records/booking/${booking.bookingID}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data && response.data.record) {
          setSelectedEICRRecord(response.data.record);
        }
      } catch (error) {
        console.error("Error fetching EICR record:", error);
      } finally {
        setLoadingEICRRecord(false);
      }
    },
    [getToken, serverURL]
  );

  const handleCancelBooking = useCallback(
    async (booking: Booking) => {
      if (cancelConfirmText !== "CANCEL BOOKING") {
        setMessage('Please type "CANCEL BOOKING" to confirm');
        setMessageType("error");
        return;
      }

      try {
        setCancellationInProgress(true);
        const token = await getToken();

        // Handle remedial bookings
        if ((booking as any).isRemedialBooking) {
          const response = await axios.post(
            `${serverURL}/admin/cancel-remedial-booking`,
            {
              remedialBookingId: (booking as any).remedialBookingId,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          // Remove from future and past bookings
          setFutureBookings((prev) =>
            prev.filter((b) => b.bookingID !== booking.bookingID)
          );
          setPastBookings((prev) =>
            prev.filter((b) => b.bookingID !== booking.bookingID)
          );

          const refundMsg = response.data.refundID
            ? `Refund issued (${response.data.refundID}). `
            : "";
          setMessage(`Remedial booking cancelled successfully. ${refundMsg}Cancellation email has been sent.`);
          setMessageType("success");
          setCancellingBooking(null);
          setCancelConfirmText("");
          return;
        }

        // Handle regular bookings
        const response = await axios.post(
          `${serverURL}/admin/cancel-booking`,
          {
            bookingID: booking.bookingID,
            payment_intent: booking.payment_intent,
            eventID: booking.eventID,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Remove from future bookings
        setFutureBookings((prev) =>
          prev.map((b) =>
            b.bookingID === booking.bookingID ? { ...b, cancelled: true } : b
          )
        );

        setMessage(response.data?.msg || "Booking cancelled successfully. Refund has been issued.");
        setMessageType("success");
        setCancellingBooking(null);
        setCancelConfirmText("");
      } catch (error: any) {
        setMessage(error.response?.data?.error || error.response?.data?.msg || "Failed to cancel booking");
        setMessageType("error");
      } finally {
        setCancellationInProgress(false);
      }
    },
    [cancelConfirmText, getToken, serverURL]
  );

  const matchesSearch = useCallback(
    (booking: Booking, query: string): boolean => {
      if (!query) return true;
      const lowerQuery = query.toLowerCase();
      return (
        booking.bookingID.includes(query) ||
        booking.customerEmail.toLowerCase().includes(lowerQuery) ||
        booking.customerName.toLowerCase().includes(lowerQuery) ||
        booking.productName.toLowerCase().includes(lowerQuery)
      );
    },
    []
  );

  const filteredFutureBookings = useMemo(
    () => futureBookings.filter((booking) => {
      if (!matchesSearch(booking, searchQuery)) return false;
      if (sourceFilter === "myconstructor") return (booking as any).fromMyConstructor === true;
      if (sourceFilter === "teck") return !(booking as any).fromMyConstructor;
      return true;
    }),
    [futureBookings, searchQuery, sourceFilter, matchesSearch]
  );

  const filteredPastBookings = useMemo(
    () => pastBookings.filter((booking) => {
      if (!matchesSearch(booking, searchQuery)) return false;
      if (sourceFilter === "myconstructor") return (booking as any).fromMyConstructor === true;
      if (sourceFilter === "teck") return !(booking as any).fromMyConstructor;
      return true;
    }),
    [pastBookings, searchQuery, sourceFilter, matchesSearch]
  );

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPageFuture(1);
    setCurrentPagePast(1);
  }, [searchQuery]);



  const renderBookingsList = (bookings: Booking[], isPast: boolean) => {
    if (loading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
          <div className="loadingBtn">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      );
    }

    if (!bookings.length) {
      return (
        <p
          style={{
            color: "#999",
            textAlign: "center",
            padding: "20px",
            fontStyle: "italic",
          }}
        >
          {isPast ? "No past bookings" : "No upcoming bookings"}
        </p>
      );
    }

    // Pagination logic for server-side pagination
    const currentPage = isPast ? currentPagePast : currentPageFuture;
    const totalPages = isPast ? totalPagesPast : totalPagesFuture;
    const handlePageChange = (newPage: number) => {
      if (isPast) {
        setCurrentPagePast(newPage);
        fetchPastPage(newPage);
      } else {
        setCurrentPageFuture(newPage);
        fetchFuturePage(newPage);
      }
    };

    return (
      <>
        {/* Pagination Controls - Moved Above Table */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "20px", padding: "0 12px", flexWrap: "wrap", gap: "8px" }}>
            <div style={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || paginating}
                style={{
                  padding: "6px 10px",
                  backgroundColor: currentPage === 1 || paginating ? "#e5e5e5" : "#00adb5",
                  color: currentPage === 1 || paginating ? "#999" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: currentPage === 1 || paginating ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "0.75em",
                  whiteSpace: "nowrap",
                }}
              >
                ← Prev
              </button>
              <div style={{ display: "flex", gap: "2px", flexWrap: "wrap", alignItems: "center" }}>
                {(() => {
                  const pages: (number | "…")[] = [];
                  for (let i = 1; i <= totalPages; i++) {
                    if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
                      pages.push(i);
                    } else if (pages[pages.length - 1] !== "…") {
                      pages.push("…");
                    }
                  }
                  return pages.map((page, idx) =>
                    page === "…" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{ padding: "4px 4px", fontSize: "0.75em", color: "#999", userSelect: "none" }}
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        disabled={paginating}
                        style={{
                          padding: "4px 8px",
                          backgroundColor: currentPage === page ? "#00adb5" : "#f0f0f0",
                          color: currentPage === page ? "white" : "#333",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          cursor: paginating ? "not-allowed" : "pointer",
                          fontWeight: currentPage === page ? "600" : "400",
                          fontSize: "0.75em",
                          minWidth: "28px",
                        }}
                      >
                        {page}
                      </button>
                    )
                  );
                })()}
              </div>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || paginating}
                style={{
                  padding: "6px 10px",
                  backgroundColor: currentPage === totalPages || paginating ? "#e5e5e5" : "#00adb5",
                  color: currentPage === totalPages || paginating ? "#999" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: currentPage === totalPages || paginating ? "not-allowed" : "pointer",
                  fontWeight: "600",
                  fontSize: "0.75em",
                  whiteSpace: "nowrap",
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        <div style={{ overflowX: "auto", borderRadius: "6px", border: "1px solid #e0e0e0", margin: "0 12px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              backgroundColor: "#fff",
              minWidth: "600px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e0e0e0" }}>
               
                <th style={{ padding: "12px 8px", textAlign: "left", fontWeight: "600", fontSize: "0.8em", color: "#333" }}>Customer</th>
                <th style={{ padding: "12px 8px", textAlign: "left", fontWeight: "600", fontSize: "0.8em", color: "#333" }}>Date</th>
                <th style={{ padding: "12px 8px", textAlign: "left", fontWeight: "600", fontSize: "0.8em", color: "#333" }}>Service</th>
                <th style={{ padding: "12px 8px", textAlign: "left", fontWeight: "600", fontSize: "0.8em", color: "#333" }}>Address</th>
                <th style={{ padding: "12px 8px", textAlign: "center", fontWeight: "600", fontSize: "0.8em", color: "#333", width: "160px", whiteSpace: "nowrap" }}>{isPast ? "Status" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking, index) => (
              <tr
                key={booking.bookingID}
                onClick={() => toggleBooking(booking)}
                style={{
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: index % 2 === 0 ? "#fff" : "#f8f9fa",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.backgroundColor = index % 2 === 0 ? "#fff" : "#f8f9fa";
                }}
              >
                
                <td style={{ textAlign: "left", padding: "12px 8px", fontSize: "0.8em", color: "#333" }}>
                  <div style={{ fontWeight: "500", marginBottom: "4px" }}>{capitaliseFirstLetter(booking.customerName)}</div>
                  <div style={{ fontSize: "0.75em", color: "#666" }}>{booking.customerEmail}</div>
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px", fontSize: "0.8em", color: "#333" }}>
                  {booking.shortDate}
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px", fontSize: "0.8em", color: "#333" }}>
                  {booking.productName}
                </td>
                <td style={{ textAlign: "left", padding: "12px 8px", fontSize: "0.8em", color: "#333" }}>
                  {(booking as any).addressLine1 ? (
                    <div>
                      <div>{(booking as any).addressLine1}</div>
                      {(booking as any).postcode && <div style={{ color: "#666", fontSize: "0.85em" }}>{(booking as any).postcode}</div>}
                    </div>
                  ) : (
                    <span style={{ color: "#999" }}>{typeof booking.address === "string" ? booking.address : "—"}</span>
                  )}
                </td>
                <td style={{ padding: "12px 8px", textAlign: "center", fontSize: "0.8em", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                  {booking.cancelled ? (
                    <span style={{ backgroundColor: "#fee2e2", color: "#dc2626", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                      CANCELLED
                    </span>
                  ) : isPast ? (
                    (() => {
                      const eicrStatus = eicrStatusMap[booking.bookingID];
                      if (!eicrStatus || !eicrStatus.found) {
                        // Check if this is an EICR-type booking
                        const isEICRBooking = booking.productName && booking.productName.toLowerCase().includes('eicr');
                        if (isEICRBooking) {
                          return (
                            <span style={{ backgroundColor: "#fef3c7", color: "#92400e", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                              No EICR Record
                            </span>
                          );
                        }
                        // Non-EICR bookings show as Complete
                        return (
                          <span style={{ backgroundColor: "#ccf2f0", color: "#00adb5", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                            COMPLETE
                          </span>
                        );
                      }
                      // If there's a certificate, show COMPLETE regardless of Pass/Fail status
                      if (eicrStatus.hasCertificate) {
                        return (
                          <span style={{ backgroundColor: "#ccf2f0", color: "#00adb5", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                            COMPLETE
                          </span>
                        );
                      }
                      if (eicrStatus.result === "Pass") {
                        return (
                          <span style={{ backgroundColor: "#dcfce7", color: "#16a34a", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                            PASS
                          </span>
                        );
                      }
                      if (eicrStatus.result === "Fail") {
                        return (
                          <span style={{ backgroundColor: "#fee2e2", color: "#dc2626", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                            FAIL
                          </span>
                        );
                      }
                      return (
                        <span style={{ backgroundColor: "#ccf2f0", color: "#00adb5", padding: "4px 10px", borderRadius: "4px", fontWeight: "600", fontSize: "0.85em" }}>
                          COMPLETE
                        </span>
                      );
                    })()
                  ) : (booking as any).isRemedialBooking ? (
                    <button
                      onClick={() => setCancellingBooking(booking)}
                      style={{
                        backgroundColor: "#fee2e2",
                        color: "#dc2626",
                        border: "1px solid #dc2626",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "0.85em",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#dc2626";
                        e.currentTarget.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fee2e2";
                        e.currentTarget.style.color = "#dc2626";
                      }}
                    >
                      Cancel
                    </button>
                  ) : (
                    <button
                      onClick={() => setCancellingBooking(booking)}
                      style={{
                        backgroundColor: "#fee2e2",
                        color: "#dc2626",
                        border: "1px solid #dc2626",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "0.85em",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#dc2626";
                        e.currentTarget.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fee2e2";
                        e.currentTarget.style.color = "#dc2626";
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div style={{ paddingBottom: "80px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Message Display */}
      {message && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: "20px",
            marginLeft: "12px",
            marginRight: "12px",
            borderRadius: "8px",
            backgroundColor: messageType === "success" ? "#d4edda" : "#f8d7da",
            color: messageType === "success" ? "#166534" : "#991b1b",
            border: `1px solid ${
              messageType === "success" ? "#c3e6cb" : "#f5c6cb"
            }`,
            fontSize: "0.9em",
          }}
        >
          {message}
        </div>
      )}

      {/* Header Section */}
      <div style={{ marginBottom: "30px", padding: "0 12px" }}>
        <p style={{ margin: "0 0 20px 0", color: "#666", fontSize: "0.85em", textAlign: "left" }}>
          Search for a booking by ID, name, email, address, date, or service name
        </p>

        {/* Search Input */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={style.searchInput}
          placeholder="Start typing to search..."
        />

        {/* Source Filter */}
        <div style={{ display: "flex", gap: "8px", marginTop: "12px", flexWrap: "wrap" }}>
          {(["all", "teck", "myconstructor"] as const).map((val) => (
            <button
              key={val}
              onClick={() => setSourceFilter(val)}
              style={{
                padding: "6px 14px",
                borderRadius: "20px",
                border: sourceFilter === val ? "none" : "1px solid #ddd",
                backgroundColor: sourceFilter === val ? "#00adb5" : "#f5f5f5",
                color: sourceFilter === val ? "#fff" : "#555",
                fontWeight: sourceFilter === val ? "600" : "400",
                fontSize: "0.8em",
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {val === "all" ? "All bookings" : val === "teck" ? "Teck Engineers only" : "MyConstructor only"}
            </button>
          ))}
        </div>
      </div>

      {/* Upcoming Bookings Section */}
      <div style={{ marginBottom: "40px", padding: "0 12px" }}>
        <h2
          style={{
            margin: "0 0 16px 0",
            color: "#333",
            fontSize: "1em",
            fontWeight: "600",
            borderBottom: "2px solid #00adb5",
            paddingBottom: "8px",
            textAlign: "left",
          }}
        >
          📅 Upcoming Bookings
        </h2>
        {renderBookingsList(filteredFutureBookings, false)}
      </div>

      {/* Past Bookings Section */}
      <div style={{ padding: "0 12px" }}>
        <h2
          style={{
            margin: "0 0 16px 0",
            color: "#333",
            fontSize: "1em",
            fontWeight: "600",
            borderBottom: "2px solid #e0e0e0",
            paddingBottom: "8px",
            textAlign: "left",
          }}
        >
          📋 Past Bookings
        </h2>
        {renderBookingsList(filteredPastBookings, true)}
      </div>

      {/* Booking Details Modal */}
      {selectedBookingForModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setSelectedBookingForModal(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              overflow: "hidden",
              maxWidth: "600px",
              width: "95%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25), 0 10px 20px rgba(0, 0, 0, 0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "20px 20px",
                background: "linear-gradient(135deg, #00adb5 0%, #008b92 100%)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                boxShadow: "0 4px 12px rgba(0, 173, 181, 0.15)",
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: "1.2em", fontWeight: "700", color: "white", letterSpacing: "0.5px" }}>
                  Booking Details
                </h2>
                <p style={{ margin: "4px 0 0 0", fontSize: "0.8em", color: "rgba(255,255,255,0.9)" }}>
                  Reference: {selectedBookingForModal!.bookingID}
                </p>
              </div>
              <button
                onClick={() => setSelectedBookingForModal(null)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "white",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  flexShrink: 0,
                  lineHeight: "1",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "20px", textAlign: "left", overflowY: "auto", flex: 1 }}>
              {/* Customer Information Section */}
              <div style={{ marginBottom: "24px" }}>
                <h3 style={{ margin: "0 0 12px 0", fontSize: "0.8em", fontWeight: "700", color: "#00adb5", textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ width: "24px", height: "2px", backgroundColor: "#00adb5" }} />
                  CUSTOMER INFORMATION
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", padding: "12px 0" }}>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "0.75em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Customer Name</p>
                    <p style={{ margin: 0, fontSize: "0.95em", fontWeight: "600", color: "#1a1a1a" }}>
                      {capitaliseFirstLetter(selectedBookingForModal!.customerName)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 4px 0", fontSize: "0.75em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Phone</p>
                    <p style={{ margin: 0, fontSize: "0.9em", color: "#555" }}>
                      {selectedBookingForModal!.customerNumber}
                    </p>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <p style={{ margin: "0 0 4px 0", fontSize: "0.75em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Email</p>
                    <p style={{ margin: 0, fontSize: "0.9em", color: "#555", wordBreak: "break-all" }}>
                      {selectedBookingForModal!.customerEmail}
                    </p>
                  </div>
                </div>
              </div>

              {/* Booking Information Section */}
              <div style={{ marginBottom: "32px" }}>
                <h3 style={{ margin: "0 0 16px 0", fontSize: "0.9em", fontWeight: "700", color: "#00adb5", textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ width: "24px", height: "2px", backgroundColor: "#00adb5" }} />
                  BOOKING INFORMATION
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", padding: "16px 0" }}>
                  <div>
                    <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Service</p>
                    <p style={{ margin: 0, fontSize: "1.05em", fontWeight: "600", color: "#1a1a1a" }}>
                      {selectedBookingForModal!.productName}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Date</p>
                    <p style={{ margin: 0, fontSize: "1.05em", fontWeight: "600", color: "#1a1a1a" }}>
                      {formatDisplayDate(selectedBookingForModal!.date)}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Time Slot</p>
                    <p style={{ margin: 0, fontSize: "1em", color: "#555" }}>
                      {selectedBookingForModal!.emailStartTime} – {selectedBookingForModal!.emailEndTime}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</p>
                    <span
                      style={{
                        backgroundColor: selectedBookingForModal!.cancelled ? "#fee2e2" : "#dcfce7",
                        color: selectedBookingForModal!.cancelled ? "#dc2626" : "#16a34a",
                        padding: "6px 12px",
                        borderRadius: "6px",
                        fontWeight: "700",
                        fontSize: "0.85em",
                        display: "inline-block",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {selectedBookingForModal!.cancelled ? "CANCELLED" : "COMPLETE"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div style={{ marginBottom: "32px" }}>
                <h3 style={{ margin: "0 0 16px 0", fontSize: "0.9em", fontWeight: "700", color: "#00adb5", textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ width: "24px", height: "2px", backgroundColor: "#00adb5" }} />
                  ADDRESS
                </h3>
                <div style={{ padding: "16px", backgroundColor: "#f8fafb", borderRadius: "8px", borderLeft: "4px solid #00adb5", lineHeight: "1.8" }}>
                  <p style={{ margin: 0, fontSize: "0.95em", color: "#333", fontWeight: "500" }}>
                    {selectedBookingForModal!.addressLine1 ? (
                      <>
                        {selectedBookingForModal!.addressLine1}
                        {selectedBookingForModal!.addressLine2 && <>, {selectedBookingForModal!.addressLine2}</>}
                        <br />
                        {selectedBookingForModal!.postTown}, {selectedBookingForModal!.postcode}
                      </>
                    ) : (
                      selectedBookingForModal!.address
                    )}
                  </p>
                </div>
              </div>

              {/* Access Notes Section */}
              {selectedBookingForModal!.access && (
                <div style={{ marginBottom: "32px" }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: "0.9em", fontWeight: "700", color: "#00adb5", textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ width: "24px", height: "2px", backgroundColor: "#00adb5" }} />
                    ACCESS NOTES
                  </h3>
                  <div style={{ padding: "16px", backgroundColor: "#fef9f0", borderRadius: "8px", borderLeft: "4px solid #f59e0b", lineHeight: "1.8" }}>
                    <p style={{ margin: 0, fontSize: "0.95em", color: "#654321" }}>
                      {selectedBookingForModal!.access}
                    </p>
                  </div>
                </div>
              )}

              {/* EICR Record Section */}
              {selectedBookingForModal?.productName.toLowerCase().includes("eicr") && (
                <>
                  <div style={{ marginBottom: "32px" }}>
                    <h3 style={{ margin: "0 0 16px 0", fontSize: "0.9em", fontWeight: "700", color: "#00adb5", textTransform: "uppercase", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ width: "24px", height: "2px", backgroundColor: "#00adb5" }} />
                      EICR RECORD
                    </h3>

                    {loadingEICRRecord ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: "12px 0", transform: "scale(0.7)", transformOrigin: "center" }}>
                        <LoadingSpinner />
                      </div>
                    ) : selectedEICRRecord ? (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px", padding: "16px 0" }}>
                          <div>
                            <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Result</p>
                            <span
                              style={{
                                backgroundColor: selectedEICRRecord.result === "Pass" ? "#dcfce7" : "#fee2e2",
                                color: selectedEICRRecord.result === "Pass" ? "#16a34a" : "#dc2626",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                fontWeight: "700",
                                fontSize: "0.9em",
                                display: "inline-block",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {selectedEICRRecord.result}
                            </span>
                          </div>
                          <div>
                            <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Inspection Date</p>
                            <p style={{ margin: 0, fontSize: "1em", color: "#333", fontWeight: "500" }}>
                              {formatDisplayDate(selectedEICRRecord.inspectionDate)}
                            </p>
                          </div>
                          <div>
                            <p style={{ margin: "0 0 6px 0", fontSize: "0.8em", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px" }}>Expiry Date</p>
                            <p style={{ margin: 0, fontSize: "1em", color: "#333", fontWeight: "500" }}>
                              {formatDisplayDate(selectedEICRRecord.expiryDate)}
                            </p>
                          </div>
                        </div>

                        {selectedEICRRecord.eicrFileUrl && (
                          <button
                            onClick={() => setSelectedEICRPdfUrl(selectedEICRRecord.eicrFileUrl)}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              backgroundColor: "#00adb5",
                              color: "white",
                              border: "none",
                              borderRadius: "8px",
                              fontSize: "0.95em",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              letterSpacing: "0.5px",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#008b92";
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 173, 181, 0.2)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#00adb5";
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            📄 View EICR Certificate PDF
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={{ padding: "16px", backgroundColor: "#f0f9ff", borderRadius: "8px", borderLeft: "4px solid #3b82f6", color: "#1e40af" }}>
                        <p style={{ margin: 0, fontSize: "0.95em" }}>
                          No EICR record found. Certificate has not yet been uploaded.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}

              <button
                onClick={() => setSelectedBookingForModal(null)}
                style={{
                  marginTop: "8px",
                  width: "100%",
                  padding: "14px 16px",
                  background: "linear-gradient(135deg, #00adb5 0%, #008b92 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "1em",
                  fontWeight: "700",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(0, 173, 181, 0.15)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 16px rgba(0, 173, 181, 0.25)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 173, 181, 0.15)";
                }}
              >
                Close Modal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EICR PDF Viewer Modal */}
      {selectedEICRPdfUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            padding: "20px",
          }}
          onClick={() => setSelectedEICRPdfUrl(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              width: "90%",
              height: "90%",
              maxWidth: "1200px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25), 0 10px 20px rgba(0, 0, 0, 0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "24px 32px",
                background: "linear-gradient(135deg, #00adb5 0%, #008b92 100%)",
                borderBottom: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "1.5em", fontWeight: "700", color: "white", letterSpacing: "0.5px" }}>
                EICR Certificate
              </h3>
              <button
                onClick={() => setSelectedEICRPdfUrl(null)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "28px",
                  cursor: "pointer",
                  color: "white",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              key={selectedEICRPdfUrl}
              src={selectedEICRPdfUrl}
              style={{
                flex: 1,
                border: "none",
                width: "100%",
              }}
              title="EICR Certificate PDF Viewer"
              onError={() => console.error("Failed to load PDF from:", selectedEICRPdfUrl)}
              onLoad={() => console.log("PDF loaded successfully from:", selectedEICRPdfUrl)}
            />
          </div>
        </div>
      )}

      {/* Cancel Booking Confirmation Modal */}
      {cancellingBooking && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            padding: "20px",
          }}
          onClick={() => !cancellationInProgress && setCancellingBooking(null)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              overflow: "hidden",
              maxWidth: "450px",
              width: "95%",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "16px 16px",
                background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.2em", fontWeight: "600", color: "white" }}>
                Cancel Booking?
              </h2>
              <button
                onClick={() => !cancellationInProgress && setCancellingBooking(null)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "24px",
                  cursor: cancellationInProgress ? "not-allowed" : "pointer",
                  color: "white",
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: cancellationInProgress ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <p style={{ margin: "0 0 12px 0", color: "#333", fontSize: "0.9em", fontWeight: "500" }}>
                <strong>{capitaliseFirstLetter(cancellingBooking!.customerName)}</strong> - Booking ID: <strong>{cancellingBooking!.bookingID}</strong>
              </p>
              <p style={{ margin: "0 0 12px 0", color: "#666", fontSize: "0.85em", lineHeight: "1.6" }}>
                You are about to cancel this booking. The customer will receive a full refund, and the event will be removed from the calendar.
              </p>
              <p style={{ margin: "0 0 16px 0", color: "#dc2626", fontSize: "0.8em", fontWeight: "600" }}>
                ⚠️ This action cannot be undone.
              </p>

              <label style={{ display: "block", marginBottom: "6px", fontWeight: "600", color: "#333", fontSize: "0.8em" }}>
                Type "CANCEL BOOKING" to confirm:
              </label>
              <input
                type="text"
                value={cancelConfirmText}
                onChange={(e) => setCancelConfirmText(e.target.value)}
                placeholder="CANCEL BOOKING"
                disabled={cancellationInProgress}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "0.85em",
                  boxSizing: "border-box",
                  marginBottom: "16px",
                  opacity: cancellationInProgress ? 0.5 : 1,
                }}
              />

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  onClick={() => setCancellingBooking(null)}
                  disabled={cancellationInProgress}
                  style={{
                    flex: 1,
                    minWidth: "100px",
                    padding: "8px 10px",
                    background: "#f3f4f6",
                    color: "#333",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "0.8em",
                    fontWeight: "600",
                    cursor: cancellationInProgress ? "not-allowed" : "pointer",
                    opacity: cancellationInProgress ? 0.5 : 1,
                  }}
                >
                  Keep Booking
                </button>
                <button
                  onClick={() => handleCancelBooking(cancellingBooking!)}
                  disabled={cancellationInProgress || cancelConfirmText !== "CANCEL BOOKING"}
                  style={{
                    flex: 1,
                    minWidth: "100px",
                    padding: "8px 10px",
                    background: cancelConfirmText === "CANCEL BOOKING" ? "#dc2626" : "#fee2e2",
                    color: cancelConfirmText === "CANCEL BOOKING" ? "white" : "#dc2626",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "0.8em",
                    fontWeight: "600",
                    cursor: cancellationInProgress || cancelConfirmText !== "CANCEL BOOKING" ? "not-allowed" : "pointer",
                    opacity: cancellationInProgress ? 0.7 : 1,
                  }}
                >
                  {cancellationInProgress ? "Cancelling..." : "Cancel Booking"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div style={{ height: "60px" }} />
    </div>
  );
}
